import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ConnectionQuality, ConnectionState, ConnectivityService, WriteFailure } from 'src/app/services/connectivity/connectivity.service';

type BannerView = 'offline' | 'weak' | 'syncing' | 'synced' | null;

// Cuánto se deja ver el "Todo sincronizado" antes de esconderse.
const SYNCED_VISIBLE_MS = 4000;
// El aviso de señal débil no se repite más seguido que esto.
const WEAK_TOAST_THROTTLE_MS = 60000;

/**
 * Aviso fijo en la parte superior con el estado de la conexión y lo que
 * falta por sincronizar. Solo aparece cuando hay algo que decir: sin
 * conexión, señal débil, o mientras se vacía la cola al recuperar la señal.
 */
@Component({
	selector: 'app-connection-banner',
	templateUrl: './connection-banner.component.html',
	styleUrls: ['./connection-banner.component.scss'],
})
export class ConnectionBannerComponent implements OnInit, OnDestroy {

	state: ConnectionState;
	view: BannerView = null;
	/** El operador escondió el aviso de señal débil; vuelve si cambia el estado. */
	weakDismissed = false;

	/** Salimos de "sin conexión" y Firestore todavía no confirmó toda la cola. */
	private recovering = false;
	private showSynced = false;
	private syncedTimer: any = null;
	private lastWeakToast = 0;
	/** Un aviso a la vez: el nuevo estado reemplaza al anterior en vez de apilarse. */
	private activeToast: HTMLIonToastElement | null = null;
	private subscriptions: Subscription[] = [];

	constructor(
		private connectivity: ConnectivityService,
		private toastCtrl: ToastController
	) { }

	ngOnInit() {
		this.subscriptions.push(
			this.connectivity.state$.subscribe(state => this.onState(state)),
			this.connectivity.synced$.subscribe(() => this.onSynced()),
			this.connectivity.writeFailed$.subscribe(failure => this.onWriteFailed(failure)),
		);
	}

	ngOnDestroy() {
		this.subscriptions.forEach(sub => sub.unsubscribe());
		if (this.syncedTimer) clearTimeout(this.syncedTimer);
	}

	// ─────────────────────────────────────────────────────────────────────
	//  Estado → vista
	// ─────────────────────────────────────────────────────────────────────

	private onState(state: ConnectionState) {
		const previousQuality: ConnectionQuality = this.state ? this.state.quality : 'online';
		this.state = state;
		if (state.quality !== previousQuality) {
			this.onQualityChange(previousQuality, state.quality);
		}
		this.refreshView();
	}

	private onQualityChange(from: ConnectionQuality, to: ConnectionQuality) {
		this.weakDismissed = false;
		if (to === 'offline') {
			this.recovering = false;
			this.hideSynced();
			this.toast('Sin conexión a internet. Puedes seguir operando: todo se guarda en este dispositivo y se enviará al volver la señal.', 'danger', 6000);
			return;
		}
		if (from === 'offline') {
			// Volvió la señal (buena o débil): se avisa y se espera a que
			// Firestore confirme la cola para decir "todo sincronizado".
			this.recovering = true;
			this.toast('Conexión restablecida. Enviando lo pendiente…', 'success', 3000);
			return;
		}
		if (to === 'weak') {
			const now = Date.now();
			if (now - this.lastWeakToast > WEAK_TOAST_THROTTLE_MS) {
				this.lastWeakToast = now;
				this.toast('Señal de internet débil. Las operaciones pueden tardar; se guardan localmente y se sincronizan solas.', 'warning', 4000);
			}
		}
	}

	private onSynced() {
		// Solo vale la pena decir "todo sincronizado" si se estaba mostrando
		// algo: al volver de offline o mientras se vaciaba una cola heredada.
		const wasVisible = this.recovering || this.view === 'syncing';
		this.recovering = false;
		if (!wasVisible) return;
		this.showSynced = true;
		if (this.syncedTimer) clearTimeout(this.syncedTimer);
		this.syncedTimer = setTimeout(() => {
			this.showSynced = false;
			this.syncedTimer = null;
			this.refreshView();
		}, SYNCED_VISIBLE_MS);
		this.refreshView();
	}

	private hideSynced() {
		this.showSynced = false;
		if (this.syncedTimer) {
			clearTimeout(this.syncedTimer);
			this.syncedTimer = null;
		}
	}

	private refreshView() {
		const state = this.state;
		if (!state) {
			this.view = null;
		} else if (state.quality === 'offline') {
			this.view = 'offline';
		} else if (state.quality === 'weak') {
			this.view = this.weakDismissed ? null : 'weak';
		} else if ((this.recovering || state.pendingFromPreviousSession) && state.syncing) {
			// Al volver la señal, o al abrir la app con cola de una sesión anterior.
			this.view = 'syncing';
		} else if (this.showSynced) {
			this.view = 'synced';
		} else {
			this.view = null;
		}
	}

	private onWriteFailed(failure: WriteFailure) {
		const code = failure.error && failure.error.code ? ` (${failure.error.code})` : '';
		this.toast(`El servidor rechazó una operación${code}: ${failure.label}. Revisa con soporte antes de repetirla.`, 'danger', 8000);
	}

	private async toast(message: string, color: string, duration: number) {
		try {
			if (this.activeToast) {
				this.activeToast.dismiss().catch(() => { });
				this.activeToast = null;
			}
			const toast = await this.toastCtrl.create({
				message,
				color,
				duration,
				// Abajo: el aviso fijo ya ocupa la parte de arriba.
				position: 'bottom',
				buttons: [{ text: 'OK', role: 'cancel' }]
			});
			this.activeToast = toast;
			toast.onDidDismiss().then(() => {
				if (this.activeToast === toast) this.activeToast = null;
			});
			await toast.present();
		} catch (error) {
			console.warn('[connection-banner] no se pudo mostrar el aviso', error);
		}
	}

	// ─────────────────────────────────────────────────────────────────────
	//  Plantilla
	// ─────────────────────────────────────────────────────────────────────

	get pendingLabel(): string {
		if (!this.state) return '';
		if (this.state.pendingWrites > 0) {
			return this.state.pendingWrites === 1 ? '1 operación por enviar' : `${this.state.pendingWrites} operaciones por enviar`;
		}
		if (this.state.pendingFromPreviousSession) {
			return 'operaciones pendientes de una sesión anterior';
		}
		return '';
	}

	get syncingTitle(): string {
		return this.recovering ? 'Conexión restablecida' : 'Sincronizando';
	}

	get latencyLabel(): string {
		if (!this.state || this.state.latency === null) return '';
		return this.state.latency >= 1000
			? `respuesta en ${(this.state.latency / 1000).toFixed(1)} s`
			: `respuesta en ${this.state.latency} ms`;
	}

	retry() {
		this.connectivity.probeNow();
	}

	dismissWeak() {
		this.weakDismissed = true;
		this.refreshView();
	}
}
